import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ToastProvider } from './components/ui';
import { initAndroidBridge, isAndroidKiosk } from './lib/androidBridge';
import { ApiError } from './lib/api';
import { AuthProvider } from './lib/auth';
import { TourProvider } from './components/Tour';
import './styles.css';

// A no-op in ordinary browsers; inside the Kortex Android kiosk it wires the
// native bridge (and routes window.print to the tablet's print path).
initAndroidBridge();
// Lets the stylesheet keep controls out of the shell's hidden top-left tap zone.
if (isAndroidKiosk()) document.documentElement.classList.add('android-kiosk');

/**
 * Phase 8 (docs/offline/PHASE-8-ARCHITECTURE.md SS4): registers the
 * hand-rolled service worker (public/sw.js) that lets a plain browser tab
 * keep loading the SPA shell without a network connection. Skipped inside
 * the Android kiosk build: it already has its own always-on local-first
 * data layer (Phase 3), it loads the SPA from a bundled virtual origin via
 * WebViewAssetLoader rather than a real network origin (docs/android/
 * architecture.md), and service-worker behaviour under that setup is
 * untested - registering there would add risk for no offline benefit.
 * Also a no-op, harmlessly, on any browser without service worker support.
 */
function registerServiceWorker(): void {
  if (isAndroidKiosk() || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      console.warn('[sw] registration failed', error);
    });
  });
}
registerServiceWorker();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      // A 401 is handled by the API client's refresh; a 403 will not fix itself.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status >= 400 && error.status < 500
          ? false
          : failureCount < 2,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            {/* Inside the router: a tour navigates between screens. */}
            <TourProvider>
              <App />
            </TourProvider>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
